import React from 'react';
import { Link } from 'react-router-dom';
import { useLogin } from '../hooks/useLogin';

function Home() {
  const { email, setEmail, password, setPassword, error, isLoggingIn, handleLogin } = useLogin();

  return (
    <div className="home-page">
      {/* Hero Section with Login */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center" style={{ minHeight: '500px' }}>
            <div className="col-lg-7 col-md-6">
              <div className="hero-content">
                <h1 className="hero-title">
                  The Church Directory<br />That Stays Current
                </h1>
                <p className="hero-subtitle">
                  No more outdated spreadsheets or printed lists. Members update their own info,
                  so your directory is always accurate and ready when you need it.
                </p>
                <div className="hero-features">
                  <div className="hero-feature">
                    <i className="fas fa-bell"></i>
                    <span>Birthday & anniversary reminders</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-magic"></i>
                    <span>Auto-extract faces from family photos</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-mobile-alt"></i>
                    <span>Beautiful iPhone & Android apps</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-gift"></i>
                    <span>Free for 3 months, then just $10/month</span>
                  </div>
                  <div className="hero-feature">
                    <i className="fas fa-users"></i>
                    <span>Unlimited member accounts — they never pay</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-5">
              <div className="card shadow-lg login-card">
                <div className="card-body">
                  <h5 className="card-title text-center mb-3">Sign In</h5>
                  {error && <div className={`alert ${error.includes('Finalizing') ? 'alert-info' : 'alert-danger'} py-2 small`}>{error}</div>}
                  <form onSubmit={handleLogin}>
                    <div className="mb-3">
                      <label htmlFor="emailLogin" className="form-label">Email</label>
                      <input type="email" className="form-control" id="emailLogin" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoggingIn} />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="passwordLogin" className="form-label">Password</label>
                      <input type="password" className="form-control" id="passwordLogin" placeholder="Enter password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isLoggingIn} />
                    </div>
                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary" disabled={isLoggingIn}>
                        {isLoggingIn ? 'Signing in...' : 'Sign In'}
                      </button>
                    </div>
                  </form>
                  <div className="text-center mt-2">
                    <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
                  </div>
                  <hr />
                  <div className="text-center">
                    <p className="signup-text mb-2">New to Church Aware?</p>
                    <Link to="/create-account" className="btn btn-success w-100">
                      Join Your Directory
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Email Linking Works */}
      <section className="email-section">
        <div className="container">
          <h2 className="text-center mb-2">Your Email Connects You</h2>
          <p className="text-center mb-4">No invite codes needed. Just use the email your church has on file.</p>
          <div className="row steps-row">
            <div className="col-md-4 text-center mb-3 mb-md-0 step-col">
              <div className="step-icon">
                <i className="fas fa-user-plus"></i>
              </div>
              <h5>1. Church Adds Your Email</h5>
              <p className="small mb-0">Your church admin adds your email to the directory.</p>
            </div>
            <div className="col-md-4 text-center mb-3 mb-md-0 step-col">
              <div className="step-icon highlighted">
                <i className="fas fa-envelope"></i>
              </div>
              <h5>2. Create Your Account</h5>
              <p className="small mb-0">Sign up with that same email address.</p>
            </div>
            <div className="col-md-4 text-center step-col step-col-last">
              <div className="step-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h5>3. Instantly Connected</h5>
              <p className="small mb-0">You're matched to your church automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Showcase */}
      <section className="showcase-section">
        <div className="container">

          {/* Row 1: Dashboard - Image Left */}
          <div className="showcase-row">
            <div className="showcase-image-wrap">
              <img src="/assets/images/Web/dashboard.png" alt="Church Aware Dashboard showing family stats, upcoming events, and birthday reminders" className="showcase-image" />
            </div>
            <div className="showcase-text">
              <h3>Your Church at a Glance</h3>
              <p>See everything that matters in one place — family count, member stats, upcoming events, and this month's birthdays and anniversaries.</p>
              <ul className="showcase-features">
                <li><i className="fas fa-check-circle"></i> Real-time membership stats</li>
                <li><i className="fas fa-check-circle"></i> Upcoming events at a glance</li>
                <li><i className="fas fa-check-circle"></i> Birthday & anniversary tracking</li>
              </ul>
            </div>
          </div>

          {/* Row 2: Families - Image Right */}
          <div className="showcase-row showcase-row-reverse">
            <div className="showcase-image-wrap">
              <img src="/assets/images/Web/families.png" alt="Church directory showing family photos, contact details, and member avatars" className="showcase-image" />
            </div>
            <div className="showcase-text">
              <h3>Beautiful Photo Directory</h3>
              <p>Browse your church family with rich photo cards. Faces are automatically extracted from family photos and can be assigned to each member. Search instantly, view contact details, and admins can download a professional PDF directory anytime.</p>
              <ul className="showcase-features">
                <li><i className="fas fa-check-circle"></i> Auto-extract faces from family photos</li>
                <li><i className="fas fa-check-circle"></i> Assign individual portraits to each member</li>
                <li><i className="fas fa-check-circle"></i> Instant search & one-click PDF download</li>
              </ul>
            </div>
          </div>

          {/* Row 3: Three-column grid - Groups, Events, Communications */}
          <div className="showcase-grid-row">
            <div className="showcase-grid-card">
              <img src="/assets/images/Web/groups.png" alt="Groups page showing Deacons and Elders with member photos" className="showcase-grid-image" />
              <h4>Organize Your People</h4>
              <p>Create groups like Deacons, Elders, or committees. See members at a glance with their photos.</p>
            </div>
            <div className="showcase-grid-card">
              <img src="/assets/images/Web/events.png" alt="Events page showing upcoming church events with dates and descriptions" className="showcase-grid-image" />
              <h4>Events & Attendance</h4>
              <p>Create church events, send notifications, and track attendance trends over time.</p>
            </div>
            <div className="showcase-grid-card">
              <img src="/assets/images/Web/communications.png" alt="Communications page showing automated birthday, anniversary, and welcome messages" className="showcase-grid-image" />
              <h4>Automated Communications</h4>
              <p>Set up automated birthday greetings, anniversary messages, and welcome emails.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Beautiful on Every Device - Mobile Apps */}
      <section className="mobile-section">
        <div className="container">
          <div className="mobile-content">
            <h2>Beautiful on Every Device</h2>
            <p className="mobile-subtitle">
              Access your church directory from anywhere with our native iPhone and Android apps.
              Get push notifications, browse offline, and call members with a single tap.
            </p>
            <div className="mobile-features-grid">
              <div className="mobile-feature-item">
                <i className="fas fa-bell"></i>
                <span>Push Notifications</span>
              </div>
              <div className="mobile-feature-item">
                <i className="fas fa-phone-alt"></i>
                <span>Tap-to-Call</span>
              </div>
              <div className="mobile-feature-item">
                <i className="fas fa-signal"></i>
                <span>Offline Access</span>
              </div>
              <div className="mobile-feature-item">
                <i className="fas fa-camera"></i>
                <span>Photo Upload</span>
              </div>
            </div>
            <div className="mobile-screenshots">
              <div className="mobile-phone-frame">
                <img src="/assets/images/Mobile/Home.png" alt="Church Aware mobile app home screen showing celebrations, events, and groups" className="mobile-screenshot-img" />
              </div>
              <div className="mobile-phone-frame">
                <img src="/assets/images/Mobile/Contacts.png" alt="Church Aware mobile app showing Contacts page with tap-to-call" className="mobile-screenshot-img" />
              </div>
              <div className="mobile-phone-frame">
                <img src="/assets/images/Mobile/Family.png" alt="Church Aware mobile app showing Families page with family photos" className="mobile-screenshot-img" />
              </div>
            </div>
            <div className="mobile-app-links">
              <a href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1" target="_blank" rel="noopener noreferrer" className="app-store-btn">
                <i className="fab fa-apple"></i> Download on the App Store
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware" target="_blank" rel="noopener noreferrer" className="app-store-btn">
                <i className="fab fa-google-play"></i> Get it on Google Play
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row g-4">
                {/* Members - Free */}
                <div className="col-md-6">
                  <div className="pricing-card pricing-card-featured text-center h-100">
                    <p className="card-context">Your church already uses Church Aware</p>
                    <h3>Join a Directory</h3>
                    <div className="price">
                      <span className="amount">Free</span>
                    </div>
                    <p className="text-muted mb-3">Always free for church members</p>
                    <ul className="pricing-list">
                      <li><i className="fas fa-check"></i> Photo directory & tap-to-call contacts</li>
                      <li><i className="fas fa-check"></i> Birthday & anniversary notifications</li>
                      <li><i className="fas fa-check"></i> Update your family's info & photos</li>
                      <li><i className="fas fa-check"></i> iPhone & Android mobile apps</li>
                    </ul>
                    <Link to="/create-account" className="btn btn-outline-primary btn-lg w-100">
                      Join Your Directory
                    </Link>
                  </div>
                </div>
                {/* Churches */}
                <div className="col-md-6">
                  <div className="pricing-card text-center h-100">
                    <p className="card-context">You're setting up your church's directory</p>
                    <h3>Create a Directory</h3>
                    <div className="price">
                      <span className="amount">$10</span>
                      <span className="period">/month for the entire church</span>
                    </div>
                    <p className="trial-badge">3-Month FREE Trial</p>
                    <ul className="pricing-list">
                      <li><i className="fas fa-check"></i> Unlimited members & admins</li>
                      <li><i className="fas fa-check"></i> Events, groups & attendance tracking</li>
                      <li><i className="fas fa-check"></i> Auto face extraction from photos</li>
                      <li><i className="fas fa-check"></i> Printable PDF directories</li>
                    </ul>
                    <Link to="/register-organization" className="btn btn-primary btn-lg w-100">
                      Register Your Church
                    </Link>
                    <p className="mt-3 text-muted small">No credit card required</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <div className="container text-center">
          <h2>Ready to Get Started?</h2>
          <p>Join churches across the country using Church Aware.</p>
          <div className="cta-buttons">
            <Link to="/create-account" className="btn btn-light btn-lg me-2">
              Join Existing Directory
            </Link>
            <Link to="/register-organization" className="btn btn-outline-light btn-lg">
              Register Your Church
            </Link>
          </div>
          <div className="app-links mt-4">
            <a href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple"></i> App Store
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-google-play"></i> Google Play
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
